import { createClient } from '@insforge/sdk';

const baseUrl = import.meta.env.VITE_INSFORGE_URL || 'https://4aqgz7mw.us-east.insforge.app';
const anonKey = import.meta.env.VITE_INSFORGE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3OC0xMjM0LTU2NzgtOTBhYi1jZGVmMTIzNDU2NzgiLCJlbWFpbCI6ImFub25AaW5zZm9yZ2UuY29tIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzIzNzgxNjd9.iLnuD53-bjLDI-gtrOFZ3iFeYZBCMoxVQPMm_4Cnp2E';

const client = createClient({ baseUrl, anonKey });

// --- AUTH STATE NOTIFIER ---
let hasAttemptedRefresh = false;
let lastSessionId = null;
const authListeners = new Set();
const notifyListeners = (event, session) => {
    const currentId = session?.user?.id || session?.id || null;
    if (event === 'SIGNED_IN' && currentId === lastSessionId) return; // Skip redundant
    if (session) lastSessionId = currentId;
    else lastSessionId = null;
    
    authListeners.forEach(cb => cb(event, session));
};

// Helper: Apply token to SDK internal state
const applyTokenToSDK = (token) => {
    if (client.http) {
        client.http.userToken = token;
    }
};

// Helper: Decode a JWT payload without verification (we trust InsForge issued it)
const decodeJWT = (token) => {
    try {
        let payload = token.split('.')[1];
        payload = payload.replace(/-/g, '+').replace(/_/g, '/');
        while (payload.length % 4) {
            payload += '=';
        }
        return JSON.parse(atob(payload));
    } catch {
        return null;
    }
};

// Helper: Check if a token is expired (with 5 minute buffer for proactive refresh)
const isTokenExpired = (token, bufferSeconds = 300) => {
    const payload = decodeJWT(token);
    if (!payload?.exp) return false; // No exp claim = doesn't expire
    const now = Math.floor(Date.now() / 1000);
    return payload.exp < (now + bufferSeconds);
};

// Helper: Exchange refresh token cookie for a new access token (standard rotation)
const getApiBase = () => {
    // In production, always use relative paths to ensure cookies are SAME-SITE (not blocked by Incognito)
    if (import.meta.env.PROD) return ''; 
    return import.meta.env.VITE_API_URL || '';
};

const refreshFromCookie = async () => {
    try {
        const apiBase = getApiBase();
        const url = `${apiBase}/api/auth/token/refresh`;
        console.log('🔄 Attemping session recovery from:', url);
        const res = await fetch(url, { 
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
        });
        
        console.log('🔄 Refresh response status:', res.status);
        if (res.ok) {
            const data = await res.json();
            if (data.accessToken) {
                console.log('✅ Session recovered successfully');
                return {
                    refreshed: true,
                    session: {
                        accessToken: data.accessToken,
                        user: data.user
                    }
                };
            } else if (data.error) {
                if (data.error === 'No refresh token') {
                    // This is totally normal for a user who isn't logged in
                    console.log('ℹ️ No active session to recover');
                } else {
                    console.warn('⚠️ Session recovery failed:', data.error);
                }
            }
        } else {
            const errText = await res.text();
            console.warn('⚠️ Session recovery failed (HTTP error):', res.status, errText);
        }
        return { refreshed: false };
    } catch (err) {
        console.error('❌ Token refresh fetch error:', err.message);
        return { refreshed: false };
    }
};

// Helper: Validate a stored token and return a user object
const validateToken = async (token) => {
    try {
        const payload = decodeJWT(token);
        if (!payload) return null;

        // Check if token is hard-expired (past exp with no buffer)
        const now = Math.floor(Date.now() / 1000);
        if (payload.exp && payload.exp < now) {
            console.warn("Token expired at", new Date(payload.exp * 1000));
            return null;
        }

        // Apply the token so subsequent SDK calls are authenticated
        applyTokenToSDK(token);

        // Build user object from JWT claims
        const user = {
            id: payload.sub,
            email: payload.email,
            role: payload.role || 'authenticated',
        };

        // Enrich with profile data from InsForge
        try {
            const { data: profile } = await client.auth.getProfile(payload.sub);
            if (profile) {
                user.profile = profile;
                user.user_metadata = profile;
            }
        } catch {
            // Profile fetch failed, but token itself is valid — proceed with basic info
            user.profile = {};
        }

        return user;
    } catch {
        applyTokenToSDK(null);
        return null;
    }
};

// Patch getSession to work with InsForge SDK
client.auth.getSession = async () => {
    try {
        // Step 0: Check URL for a token (OAuth callback result)
        // This makes the transition from Google redirect to logged-in state instant
        const urlParams = new URLSearchParams(window.location.search);
        const tokenFromUrl = urlParams.get('access_token');
        if (tokenFromUrl) {
            localStorage.setItem('insforge_session_token', tokenFromUrl);
            // Proactively refresh from cookie in background to ensure we have a valid rotation token
            refreshFromCookie().catch(() => {});
        }

        // Step 1: Check localStorage for a stored token (fastest path for OAuth callbacks)
        const storedToken = localStorage.getItem('insforge_session_token');
        
        if (storedToken && !isTokenExpired(storedToken, 0)) {
            // Token appears valid, try to use it directly
            applyTokenToSDK(storedToken);
            try {
                // If token is close to expiry (within 5 mins), proactively refresh proxy cookie in background
                if (isTokenExpired(storedToken, 300)) {
                    refreshFromCookie().catch(() => {});
                }
                
                const user = await validateToken(storedToken);
                if (user) {
                    const session = { accessToken: storedToken, user };
                    if (user?.id !== lastSessionId) notifyListeners('SIGNED_IN', session);
                    return { data: { session }, error: null };
                }
            } catch (err) {
                console.warn("Stored token validation failed (network or other):", err.message);
                // If it was just a network error, validateToken returns null. We fall through to next steps.
            }
        }

        // Step 2: Try custom proxy server cookie refresh (Google OAuth)
        // Only attempt this once per page load for guest users (no token) to avoid infinite loops
        if (!hasAttemptedRefresh) {
            hasAttemptedRefresh = true;
            const proxyCheck = await refreshFromCookie();
            if (proxyCheck.refreshed && proxyCheck.session) {
                const session = proxyCheck.session;
                localStorage.setItem('insforge_session_token', session.accessToken);
                applyTokenToSDK(session.accessToken);

                if (session.user?.id !== lastSessionId) {
                    notifyListeners('SIGNED_IN', session);
                }
                return { data: { session }, error: null };
            }
        }

        // No valid token anywhere = no session
        localStorage.removeItem('insforge_session_token');
        applyTokenToSDK(null);
        return { data: { session: null }, error: null };
    } catch (err) {
        localStorage.removeItem('insforge_session_token');
        applyTokenToSDK(null);
        return { data: { session: null }, error: err };
    }
};

client.auth.onAuthStateChange = (callback) => {
    authListeners.add(callback);
    return { 
        data: { 
            subscription: { 
                unsubscribe: () => { authListeners.delete(callback); } 
            } 
        } 
    };
};

// Wrap auth methods to reset hasAttemptedRefresh on state changes
const originalSignOut = client.auth.signOut;
client.auth.signOut = async (...args) => {
    hasAttemptedRefresh = false;
    return originalSignOut.apply(client.auth, args);
};

const originalSignInWithPassword = client.auth.signInWithPassword;
client.auth.signInWithPassword = async (...args) => {
    hasAttemptedRefresh = false;
    return originalSignInWithPassword.apply(client.auth, args);
};

const originalSignUp = client.auth.signUp;
client.auth.signUp = async (...args) => {
    hasAttemptedRefresh = false;
    return originalSignUp.apply(client.auth, args);
};

export const supabase = client;
